import { JobDetailsV2Fragment } from "../../generated/graphql.generated";

import type { QueuedNavigatorWrite } from "../job/job-navigator/navigator-outbox/navigator-outbox.util";

import type { UnsavedChange } from "./unsaved-changes.reducer";

export type LastOpenedJobPage = 'view' | 'edit';

export interface PersistedState {
  pinnedJobs: string[];
  recentJobs: string[];
  lastOpenedJobPageByJobId: Record<string, LastOpenedJobPage>;
  jobs: Record<string, JobDetailsV2Fragment>;
  queue: UnsavedChange[];
  /** The crew's navigator saves waiting for signal (#86a) — every job's, not only pinned or recent ones. */
  navigatorOutbox: QueuedNavigatorWrite[];
}
export const PERSIST_KEY = 'persist:v2.3';
const PERSIST_KEY_PATTERN = /^persist:v(\d+(?:\.\d+)*)$/;

function isOlderPersistKey(key: string): boolean {
  const keyMatch = PERSIST_KEY_PATTERN.exec(key);
  const currentKeyMatch = PERSIST_KEY_PATTERN.exec(PERSIST_KEY);

  if (!keyMatch || !currentKeyMatch) { return false; }

  const keyVersion = keyMatch[1].split('.').map(Number);
  const currentVersion = currentKeyMatch[1].split('.').map(Number);
  const segmentCount = Math.max(keyVersion.length, currentVersion.length);

  for (let index = 0; index < segmentCount; index++) {
    const keySegment = keyVersion[index] ?? 0;
    const currentSegment = currentVersion[index] ?? 0;

    if (keySegment !== currentSegment) {
      return keySegment < currentSegment;
    }
  }

  return false;
}

export function removeStalePersistedState(): void {
  try {
    // Requirement: Never remove current or future persisted-state versions;
    // mixed-version clients and deployment rollbacks may still depend on them.
    Object.keys(localStorage)
      .filter(isOlderPersistKey)
      .forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.warn('Failed to remove stale persisted state', error);
  }
}

const stringifiedState = localStorage.getItem(PERSIST_KEY);

export function loadFromStorage<K extends keyof PersistedState>(key: K, fallback: PersistedState[K]): PersistedState[K] {
  if (!stringifiedState) { return fallback };
  try {
    const persistedState: PersistedState = JSON.parse(stringifiedState);
    return persistedState[key] ?? fallback;
  } catch {
    return fallback;
  }
}
